import type { DadosEventoCalendar } from "./types";

const SP_TIMEZONE = "America/Sao_Paulo";

export function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: SP_TIMEZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
}

export function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: SP_TIMEZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(iso));
}

export function formatRelativeToNow(iso: string | null): string {
  if (!iso) return "—";
  const diffMs = new Date(iso).getTime() - Date.now();
  const diffMin = Math.round(diffMs / 60000);
  const abs = Math.abs(diffMin);

  if (abs < 1) return "agora";
  if (abs < 60) return diffMin < 0 ? `há ${abs} min` : `em ${abs} min`;

  const diffHoras = Math.round(diffMin / 60);
  if (Math.abs(diffHoras) < 24) {
    return diffHoras < 0 ? `há ${Math.abs(diffHoras)}h` : `em ${diffHoras}h`;
  }

  const diffDias = Math.round(diffHoras / 24);
  return diffDias < 0 ? `há ${Math.abs(diffDias)}d` : `em ${diffDias}d`;
}

/** Dias desde uma data ISO ate agora, arredondado para baixo, minimo 0. */
export function diasDesde(iso: string): number {
  const diffMs = Date.now() - new Date(iso).getTime();
  return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
}

/** Inicio (00:00) e fim (23:59:59.999) do dia de hoje, no fuso de Sao Paulo,
 * como Date UTC equivalentes — usado para separar Atrasados / Hoje na fila. */
export function limitesDeHojeSaoPaulo(): { inicio: Date; fim: Date } {
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: SP_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const d = parts.find((p) => p.type === "day")?.value;

  // America/Sao_Paulo e UTC-03:00 o ano todo desde o fim do horario de verao
  // brasileiro (2019). Fixar o offset e uma simplificacao aceitavel aqui: o
  // pior caso e a fila trocar de grupo (Atrasado/Hoje) com ate 1h de folga,
  // o que nao muda nenhuma regra de escrita, so o agrupamento visual.
  const inicio = new Date(`${y}-${m}-${d}T00:00:00.000-03:00`);
  const fim = new Date(`${y}-${m}-${d}T23:59:59.999-03:00`);
  return { inicio, fim };
}

/** Converte um ISO (UTC) para o formato aceito por <input type="datetime-local">
 * (hora LOCAL do navegador, sem timezone). Usado para pre-preencher um campo
 * de edicao a partir de um valor ja salvo — para escrever, o valor do input
 * ja sai pronto para `new Date(valor).toISOString()`. */
export function paraInputDatetimeLocal(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes(),
  )}`;
}

export function formatCurrency(valor: number | null): string {
  if (valor === null) return "—";
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(valor);
}

export function normalizarTelefoneParaLink(telefone: string): string {
  const digitos = telefone.replace(/\D/g, "");
  if (digitos.length >= 12 && digitos.startsWith("55")) return digitos;
  return `55${digitos}`;
}

export function telHref(telefone: string): string {
  return `tel:+${normalizarTelefoneParaLink(telefone)}`;
}

export function waHref(telefone: string, mensagem: string): string {
  const numero = normalizarTelefoneParaLink(telefone);
  return `https://wa.me/${numero}?text=${encodeURIComponent(mensagem)}`;
}

export function mensagemPadraoWhatsapp(empresa: string, pessoaContato: string | null): string {
  const saudacao = pessoaContato ? `Olá, ${pessoaContato}!` : "Olá!";
  return `${saudacao} Aqui é da Café Labs. Podemos conversar sobre ${empresa}?`;
}

/** Formata um instante ISO como "YYYYMMDDTHHMMSSZ" (UTC) — formato exigido
 * pelo parametro `dates` do template do Google Calendar. */
function paraGoogleCalendarUTC(iso: string): string {
  return new Date(iso).toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
}

/** Caminho A do Google Calendar: monta os dados do evento 100% no client, a
 * partir do proprio lead ja carregado — sem RPC, sem backend. Porte de
 * dados_evento_calendar() (supabase/migrations/20260903100500_v11_google_calendar.sql,
 * mantido no historico do git); so o "caminho A" (link de template, sem
 * OAuth) sobreviveu a migracao pra Firebase — o "caminho B" (fila +
 * credencial do Google) dependia de Edge Function, fora do escopo do plano
 * Spark. */
export function montarDadosEventoCalendar(
  lead: { id: string; empresa: string; pessoa_contato: string | null; telefone: string | null; email: string | null; site: string | null; ramo: string | null; cidade: string | null; uf: string | null; reuniao_em: string | null },
  duracaoMin = 60,
): DadosEventoCalendar | null {
  if (!lead.reuniao_em) return null;

  const inicio = new Date(lead.reuniao_em);
  const fim = new Date(inicio.getTime() + duracaoMin * 60_000);

  const detalhes = [
    `Lead: ${lead.empresa}`,
    lead.pessoa_contato ? `Contato: ${lead.pessoa_contato}` : null,
    lead.telefone ? `Telefone: ${lead.telefone}` : null,
    lead.email ? `E-mail: ${lead.email}` : null,
    lead.site ? `Site: ${lead.site}` : null,
    lead.ramo ? `Ramo: ${lead.ramo}` : null,
  ]
    .filter((v): v is string => v !== null)
    .join("\n");

  const local = [lead.cidade, lead.uf].filter(Boolean).join(" / ") || null;

  return {
    lead_id: lead.id,
    titulo: `Reunião - ${lead.empresa}`,
    detalhes: detalhes || null,
    local,
    inicio: inicio.toISOString(),
    fim: fim.toISOString(),
    dates: `${paraGoogleCalendarUTC(inicio.toISOString())}/${paraGoogleCalendarUTC(fim.toISOString())}`,
    template_base: "https://calendar.google.com/calendar/render?action=TEMPLATE",
  };
}

/** `dates` ja vem no formato exigido pelo Google e NAO deve ser encodado (a
 * barra faz parte do formato); os demais campos passam por
 * encodeURIComponent normalmente. */
export function montarUrlGoogleCalendar(dados: DadosEventoCalendar): string {
  const params = [
    `text=${encodeURIComponent(dados.titulo)}`,
    `dates=${dados.dates}`,
    `details=${encodeURIComponent(dados.detalhes ?? "")}`,
    `location=${encodeURIComponent(dados.local ?? "")}`,
  ];
  return `${dados.template_base}&${params.join("&")}`;
}
